import type { Trade as SendingTrade } from 'screens/Main/tabs/Send/ActiveTradesPage';
import type { ReceivingTrade } from 'screens/Main/tabs/Receive/ActiveReceivingPage';
import type { ThreadStatusTone, TradeRole, TradeThreadContext } from 'types/interfaces/thread';
import { TRADE_STATUS_CHIP } from 'screens/Main/tabs/Inbox/threadStatus';

/**
 * What the Contact modal and the Summon Admin wizard know about the trade
 * they were opened from (Asana 1219952262403623, Decisions 15 to 17). Built
 * by the Sending and Receiving pages from the trade objects they already
 * hold, so opening the modal costs no request.
 */
export interface ContactTradeItem {
  /** TradeItem id when the source payload carries one; the wizard's affected-card checklist needs it. */
  id?: string;
  name: string;
  quantity: number;
}

export interface ContactTradeSummary {
  tradeId: string;
  tradeNumber: string;
  role: TradeRole;
  partnerUsername: string;
  statusLabel: string;
  statusTone: ThreadStatusTone;
  /** ISO timestamp or server-formatted date string, whatever the page holds. */
  shippedAt?: string | null;
  /** Server-formatted estimated arrival, rendered verbatim. */
  estimatedArrivalLabel?: string | null;
  /** Only the thread context carries it; the wizard shows Tracking when present. */
  trackingNumber?: string | null;
  totalValue: number;
  items: ContactTradeItem[];
}

export const summaryFromSendingTrade = (trade: SendingTrade): ContactTradeSummary => {
  const status: Record<SendingTrade['status'], { label: string; tone: ThreadStatusTone }> = {
    ready: trade.awaitingFunding ? { label: 'Awaiting funding', tone: 'purple' } : { label: 'Ready to ship', tone: 'blue' },
    transit: { label: 'Shipped', tone: 'orange' },
    completed: { label: 'Completed', tone: 'green' },
    cancelled: { label: 'Cancelled', tone: 'grey' },
  };
  const chip = status[trade.status] ?? { label: trade.status, tone: 'grey' };
  return {
    tradeId: trade.id,
    tradeNumber: trade.tradeNumber,
    role: 'SENDER',
    partnerUsername: trade.traderName,
    statusLabel: chip.label,
    statusTone: chip.tone,
    shippedAt: trade.shipped ?? null,
    estimatedArrivalLabel: trade.estimatedDelivery ?? null,
    totalValue: trade.totalValue,
    items: trade.items.map((item) => ({ id: item.id, name: item.name, quantity: item.qty })),
  };
};

export const summaryFromReceivingTrade = (trade: ReceivingTrade): ContactTradeSummary => {
  const status: Record<ReceivingTrade['status'], { label: string; tone: ThreadStatusTone }> = {
    preparing: { label: 'Preparing', tone: 'blue' },
    transit: { label: 'Shipped', tone: 'orange' },
    completed: { label: 'Completed', tone: 'green' },
    cancelled: { label: 'Cancelled', tone: 'grey' },
  };
  const chip = status[trade.status] ?? { label: trade.status, tone: 'grey' };
  return {
    tradeId: trade.id,
    tradeNumber: trade.tradeNumber,
    role: 'RECEIVER',
    partnerUsername: trade.traderName,
    statusLabel: chip.label,
    statusTone: chip.tone,
    shippedAt: trade.shipped ?? null,
    estimatedArrivalLabel: trade.estimatedDelivery ?? null,
    totalValue: trade.totalValue,
    items: trade.items.map((item) => ({ id: item.id, name: item.name, quantity: item.qty })),
  };
};

/** The rail and the sheet open the wizard from the thread's TRADE context. */
export const summaryFromTradeContext = (trade: TradeThreadContext, partnerUsername: string): ContactTradeSummary => {
  const chip = TRADE_STATUS_CHIP[trade.status.toUpperCase()] ?? { label: trade.status, tone: 'grey' as ThreadStatusTone };
  return {
    tradeId: trade.tradeId,
    tradeNumber: trade.tradeNumber,
    role: trade.role,
    partnerUsername,
    statusLabel: chip.label,
    statusTone: chip.tone,
    shippedAt: trade.shippedAt,
    estimatedArrivalLabel: trade.estimatedArrivalLabel,
    trackingNumber: trade.trackingNumber,
    totalValue: trade.totalValue,
    items: trade.items
      .filter((item) => !item.removedAt)
      .map((item) => ({ id: item.id, name: item.name, quantity: item.quantity })),
  };
};

export const cardCountOf = (summary: ContactTradeSummary): number =>
  summary.items.reduce((sum, item) => sum + (item.quantity || 1), 0);

/** "You sent · Shipped Oct 8" or "You're receiving · Not shipped yet". */
export const shipLine = (summary: ContactTradeSummary): string => {
  const who = summary.role === 'SENDER' ? 'You sent' : "You're receiving";
  if (!summary.shippedAt) return `${who} · Not shipped yet`;
  const date = new Date(summary.shippedAt);
  const shipped = Number.isNaN(date.getTime())
    ? summary.shippedAt
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${who} · Shipped ${shipped}`;
};
